import {yupResolver} from "@hookform/resolvers/yup";
import {useFieldArray, useForm, Controller} from "react-hook-form";
import {CloseOutlined, PlusCircleFilled} from "@ant-design/icons";
import {Button, Space, Col, Row, Select} from "antd";
import {step1Schema} from "./step-1-form-validation-schema";
import {CustomInputComponent} from "../../../components/input/input-component";
import {CustomSelectInput} from "../../../components/select";
import {SocialOptions} from "./step-1-utils";
import {useSelector} from "react-redux";
import {RootState} from "../../../redux/store";
import {useEffect, useRef, useState} from "react";
import {TProfilePayload} from "../../../redux/profile/i-profile-1";
import useDebounce from "../../../hooks/use-debounce";
import {
  TEditProfileFormProps,
  TProfileSocial,
} from "../../../components/profile/edit-profile/edit-profile-form";
import {
  useUpdateUserProfile,
  useLegacyVerifyUsername,
} from "../../../features/profile/hooks/useProfile";
import {IEditProfileFormValues} from "../../../components/profile/edit-profile/edit-profile";
import {
  buildProfileSocialsObject,
  buildSocialsObject,
  handleAddNewSocialLink,
  handleRemoveSocialLink,
  handleSocialSelectOnChange,
  updateSocialOptionsState,
} from "../../../utils/profile";
import {AvatarUpload} from "../../../components/profile/edit-profile/avatar-upload";
import {ProfileCoverImageUpload} from "../../../components/profile/edit-profile/cover-image-upload";
import {REBRAND_BASE_URL} from "../../../services/config";
import {FormField, FormFieldError} from "../../../design-system/form-field";
import {GetUserProfileApiResponse} from "../../../react-query-hooks/profile/types";
import {OnboardingPublicDisplayNameFormField} from "./onboard-public-display-name";

declare const window: Window &
  typeof globalThis & {
    Intercom: any;
    heap: any;
  };

const {Option} = Select;
// import {useOnboardingData} from "../oboarding-context";

type TStep1FormProps = {
  updateStep: (step: number) => void;
  profileData: GetUserProfileApiResponse;
} & TEditProfileFormProps;

export const HowDidYouHearAboutUsOptions = [
  {
    label: "Linkedin",
    value: "linkedin",
  },
  {
    label: "Instagram",
    value: "instagram",
  },
  {
    label: "YouTube",
    value: "youTube",
  },
  {
    label: "X (Formerly Twitter)",
    value: "X",
  },
  {
    label: "Influencer",
    value: "influencer",
  },
  {
    label: "Referral",
    value: "referral",
  },
  {
    label: "Others",
    value: "other",
  },
];

const Step1Form: React.FC<TStep1FormProps> = ({updateStep, countries, profileData}) => {
  const [socialOptions, setSocialOptions] = useState(SocialOptions);
  const updateProfileQueryMutation = useUpdateUserProfile();

  const {userInfo} = useSelector((state: RootState) => state.userLogin);

  const usernameString =
    profileData.username && profileData.username.split("https://www.coachli.co/");

  function handleHowDidYouHearAboutUs() {
    if (profileData.how_did_you_find_us) {
      const valueExists = HowDidYouHearAboutUsOptions.find(
        (x) => x.value === profileData.how_did_you_find_us
      );
      if (valueExists) {
        return valueExists.value;
      } else {
        return "other";
      }
    } else {
      return "";
    }
  }
  function handleHowDidYouHearAboutUsOther() {
    if (profileData.how_did_you_find_us) {
      const finalVal = handleHowDidYouHearAboutUs() === "other";

      if (finalVal) {
        return profileData.how_did_you_find_us;
      } else {
        return "";
      }
    } else {
      return "";
    }
  }

  const {
    handleSubmit,
    formState: {errors},
    watch,
    control,
    setError,
    clearErrors,
    getValues,
    reset,
  } = useForm<IEditProfileFormValues>({
    resolver: yupResolver(step1Schema),
    mode: "onChange",
    defaultValues: {
      firstName: profileData.firstName,
      lastName: profileData.lastName,
      country: profileData.country,
      displayName: profileData.display_name!,
      username: usernameString ? usernameString[0] : "",
      shortDescription: profileData.description,
      shortBio: profileData.shortBio,
      how_did_you_find_us: handleHowDidYouHearAboutUs(),
      how_did_you_find_us_other: handleHowDidYouHearAboutUsOther(),

      socials: [...buildProfileSocialsObject(profileData)],
    },
  });

  const {fields, append, remove} = useFieldArray({
    control,
    name: "socials",
  });

  const socialsArrayValue = watch("socials");
  const usernameValue: string = watch("username").toLowerCase();
  const deb = useDebounce(usernameValue.toLowerCase(), 1000);
  // Only check availability once the debounced value settles at 4+ chars,
  // matching the previous `deb.length > 3` gate on the redux thunk dispatch.
  const usernameToVerify = deb && deb.length > 3 ? deb : "";

  const {
    isSuccess: isSuccessUsername,
    isError: isErrorUsername,
    error: verifyUsernameError,
    data: verifyUsernameData,
  } = useLegacyVerifyUsername(usernameToVerify);

  const isUsernameTaken = isSuccessUsername && verifyUsernameData?.isAvailable === false;

  // TO ALWAYS KEEP THE SOCIAL OPTIONS UP TO DATE WITH NEW VALUES
  useEffect(() => {
    updateSocialOptionsState(socialsArrayValue, setSocialOptions);
  }, [socialsArrayValue]);

  // Sync form with server data when profileData changes (e.g. navigating back from Step 2).
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const uStr =
      profileData.username && profileData.username.split("https://www.coachli.co/");
    reset({
      firstName: profileData.firstName,
      lastName: profileData.lastName,
      country: profileData.country,
      displayName: profileData.display_name!,
      username: uStr ? uStr[0] : "",
      shortDescription: profileData.description,
      shortBio: profileData.shortBio,
      how_did_you_find_us: handleHowDidYouHearAboutUs(),
      how_did_you_find_us_other: handleHowDidYouHearAboutUsOther(),
      socials: [...buildProfileSocialsObject(profileData)],
    });
  }, [profileData]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit = async (values: IEditProfileFormValues) => {
    const {
      firstName,
      country,
      lastName,
      shortBio,
      shortDescription,
      socials,
      username,
      how_did_you_find_us,
      how_did_you_find_us_other,
      displayName,
    } = values;
    const socialValues = socials;
    const socialObject: TProfileSocial = {
      twitter: null,
      facebook: null,
      instagram: null,
      linkedin: null,
      tiktok: null,
      discord: null,
      youtube: null,
      dribble: null,
      behance: null,
      twitch: null,
      github: null,
      website: null,
    };

    if (socialValues.length > 0) {
      socialValues.forEach((elem, i) => {
        if (elem.name === "Dribbble") {
          socialObject["dribble"] = elem.link;
        } else {
          socialObject[elem.name.toLowerCase() as keyof typeof socialObject] = elem.link;
        }
      });
    }

    // console.log({...values, ...socialObject});
    const payload: TProfilePayload = {
      firstName,
      country,
      lastName,
      shortBio,
      shortDescription,
      username,
      display_name: displayName,
      how_did_you_find_us:
        how_did_you_find_us === "other" ? how_did_you_find_us_other : how_did_you_find_us,
      ...socialObject,
    };

    updateProfileQueryMutation.mutate(payload, {
      onSuccess: (data) => {
        // console.log("step_1_update profile data", data);
        window.heap.identify(
          `${userInfo?.user?.id}: ${data.data.firstName} ${data.data.lastName}`
        );
        window.Intercom("update", {
          // api_base: "https://api-iam.intercom.io",
          // app_id: "lhv6fa0p",
          name: `${data.data.firstName} ${data.data.lastName}`, // Full name
          email: `${data.data.email}`, // Email address
          user_id: `${userInfo?.user?.id}`,
        });
        updateStep(2);
      },
    });
  };

  return (
    <Space direction="vertical" size={24} style={{display: "flex"}}>
      <ProfileCoverImageUpload />
      <AvatarUpload username={usernameValue} />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Space direction="vertical" size={29} style={{display: "flex"}}>
          <OnboardingPublicDisplayNameFormField
            error={errors?.displayName}
            control={control}
            watch={watch}
            setError={setError}
            clearErrors={clearErrors}
            firstName={getValues("firstName")}
            lastName={getValues("lastName")}
          />

          <CustomInputComponent
            id="username"
            name="username"
            label="Username"
            placeholder="your username"
            error={
              errors?.username
                ? errors.username.message
                : isErrorUsername
                ? verifyUsernameError?.message
                : isUsernameTaken
                ? "Username is already taken"
                : ""
            }
            control={control}
            addonBefore={`${REBRAND_BASE_URL}/`}
          />
          <Row gutter={[16, 29]}>
            <Col span={24} md={12}>
              {" "}
              <CustomInputComponent
                id="firstName"
                label="First name"
                placeholder="Enter your first name"
                error={errors.firstName?.message}
                control={control}
                name="firstName"
              />
            </Col>
            <Col span={24} md={12}>
              <CustomInputComponent
                id="lastName"
                name="lastName"
                label="Last name"
                placeholder="Enter your last name"
                error={errors.lastName?.message}
                control={control}
              />
            </Col>
          </Row>

          <CustomSelectInput
            control={control}
            id="country"
            label="Country"
            name="country"
            placeholder="Select your Country"
            options={countries}
            showSearch
            error={errors.country?.message}
            style={{width: "100%"}}
          />
          <CustomInputComponent
            id="shortBio"
            name="shortBio"
            type="textarea"
            label="Short bio"
            placeholder="Tell your audience about yourself"
            error={errors.shortBio?.message}
            control={control}
          />

          <CustomInputComponent
            id="description"
            name="shortDescription"
            label="Short description"
            placeholder="E.g UX Designer"
            error={errors.shortDescription?.message}
            control={control}
          />

          {fields.slice(0, 12).map((item, index) => (
            <div key={item.id} style={{display: "flex", gap: "1.6rem"}}>
              {/* // <Space size={16} key={item.id}> */}
              <div style={{flex: "1"}}>
                <CustomInputComponent
                  id={`socials[${index}].value`}
                  name={`socials[${index}].link`}
                  placeholder="your profile link"
                  error={errors.socials?.[index]?.link?.message}
                  control={control}
                  // style={{width: '9'}}
                  addonBefore={
                    <Controller
                      control={control}
                      name={`socials.${index}.name`}
                      render={({field: {onChange, value, ref}}) => (
                        <>
                          <Select
                            onChange={(val) =>
                              handleSocialSelectOnChange(
                                socialsArrayValue,
                                setSocialOptions,
                                onChange,
                                val
                              )
                            }
                            value={value}
                            ref={ref}
                            size="large"
                            status={errors.socials?.[index]?.name?.message ? "error" : ""}
                            showSearch={false}
                            notFoundContent={null}
                            placeholder={""}
                            optionFilterProp="children"
                            filterOption={(input, option) =>
                              (option!.children as unknown as string)
                                .toLowerCase()
                                .includes(input.toLowerCase())
                            }
                          >
                            {socialOptions.map((option, optionIdx: number | string) => (
                              <Option key={optionIdx} value={option.name}>
                                {option.name}
                              </Option>
                            ))}
                          </Select>
                          {errors.socials?.[index]?.name?.message && (
                            <div role="alert" className="ant-form-item-explain-error">
                              {errors.socials?.[index]?.name?.message}
                            </div>
                          )}
                        </>
                      )}
                    />
                  }
                />
              </div>

              <Button
                onClick={() =>
                  handleRemoveSocialLink(
                    socialsArrayValue,
                    setSocialOptions,
                    remove,
                    index
                  )
                }
                type="link"
                shape="circle"
                icon={<CloseOutlined />}
                size="small"
              />
              {/* </Space> */}
            </div>
          ))}
          {fields.length < 12 && (
            <Button
              icon={<PlusCircleFilled />}
              onClick={() =>
                handleAddNewSocialLink(socialsArrayValue, setSocialOptions, append)
              }
              size="small"
            >
              Add social media
            </Button>
          )}
          <FormField
            id="howDidYou"
            label="How did you hear about Coachli?"
            renderInput={(id) => (
              <Controller
                control={control}
                name="how_did_you_find_us"
                render={({
                  field: {onChange, value, ref, onBlur},
                  fieldState: {error},
                }) => (
                  <Space direction="vertical" size={16} style={{display: "flex"}}>
                    <Select
                      id={id}
                      size="large"
                      status={error?.message ? "error" : undefined}
                      style={{width: "100%"}}
                      onChange={onChange}
                      value={value}
                      ref={ref}
                      onBlur={onBlur}
                      options={HowDidYouHearAboutUsOptions}
                      placeholder="Please select"
                    />
                    <FormFieldError errorMessage={error?.message} />
                  </Space>
                )}
              />
            )}
          />

          {watch("how_did_you_find_us") === "other" && (
            <CustomInputComponent
              id="how_did_you_find_us_other"
              placeholder="Please enter"
              error={errors.how_did_you_find_us_other?.message}
              control={control}
              name="how_did_you_find_us_other"
            />
          )}

          <Row gutter={[0, 16]}>
            <Col span={24} sm={12}>
              <Button
                loading={updateProfileQueryMutation.isLoading}
                size="small"
                block
                type="primary"
                htmlType="submit"
              >
                Save and continue
              </Button>
            </Col>
          </Row>
        </Space>
      </form>
    </Space>
  );
};

export {Step1Form};
